import { useState, useMemo } from 'react';
import styled, { css } from 'foundations-theming/utils';
import { Small } from 'foundations-components/transitional/components/Small';
import { RightIcon } from 'foundations-assets/icons/components/RightIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const MAX_DEPTH = 20;
function pluralize(count, singular) {
  return `${count} ${singular}${count === 1 ? '' : 's'}`;
}
function summarize(value) {
  if (Array.isArray(value)) return `[ ... ${pluralize(value.length, 'item')} ]`;
  if (typeof value === 'object' && value !== null) return `{ ... ${pluralize(Object.keys(value).length, 'key')} }`;
  return String(value);
}
function isPrimitive(value) {
  return value === null || typeof value !== 'object';
}
const nodeBase = css(["display:flex;align-items:baseline;gap:4px;font-family:monospace;font-size:12px;line-height:1.6;overflow:hidden;"]);
const Row = styled.button.withConfig({
  displayName: "PrettyPrintJson__Row"
})(["", " width:100%;padding:2px 0 2px ", "px;border:none;background:transparent;cursor:pointer;text-align:left;color:inherit;border-radius:2px;&:hover{background:rgba(0,0,0,0.04);}"], nodeBase, ({
  $depth
}) => $depth * 16);
const PrimitiveRow = styled.span.withConfig({
  displayName: "PrettyPrintJson__PrimitiveRow"
})(["", " padding:2px 0 2px ", "px;"], nodeBase, ({
  $depth
}) => $depth * 16 + 20);
const Chevron = styled.span.withConfig({
  displayName: "PrettyPrintJson__Chevron"
})(["display:inline-flex;align-items:center;justify-content:center;width:16px;height:16px;flex-shrink:0;transition:transform 0.12s ease;transform:rotate(", ");svg{width:10px;height:10px;}"], ({
  $open
}) => $open ? '90deg' : '0deg');
const StringValue = styled.span.withConfig({
  displayName: "PrettyPrintJson__StringValue"
})(["color:", ";word-break:break-all;"], ({
  theme
}) => theme.color['fill-positive-default']);
const NumberValue = styled.span.withConfig({
  displayName: "PrettyPrintJson__NumberValue"
})(["color:", ";"], ({
  theme
}) => theme.color['fill-info-default']);
const BoolValue = styled.span.withConfig({
  displayName: "PrettyPrintJson__BoolValue"
})(["color:", ";"], ({
  theme
}) => theme.color['text-alert-default']);
const NullValue = styled.span.withConfig({
  displayName: "PrettyPrintJson__NullValue"
})(["color:", ";"], ({
  theme
}) => theme.color['fill-accent-purple-default']);
function ValueDisplay({
  value
}) {
  if (value === null) return /*#__PURE__*/_jsx(NullValue, {
    children: "null"
  });
  if (typeof value === 'string') return /*#__PURE__*/_jsxs(StringValue, {
    children: ["\"", value, "\""]
  });
  if (typeof value === 'number') return /*#__PURE__*/_jsx(NumberValue, {
    children: value
  });
  if (typeof value === 'boolean') return /*#__PURE__*/_jsx(BoolValue, {
    children: String(value)
  });
  return /*#__PURE__*/_jsx("span", {
    children: String(value)
  });
}
function TreeNode({
  label,
  value,
  depth,
  defaultOpen
}) {
  const [open, setOpen] = useState(defaultOpen);
  if (isPrimitive(value)) {
    return /*#__PURE__*/_jsxs(PrimitiveRow, {
      $depth: depth,
      children: [label && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsxs(Small, {
          weight: "demi",
          children: [label, ":"]
        }), ' ']
      }), /*#__PURE__*/_jsx(ValueDisplay, {
        value: value
      })]
    });
  }
  if (depth >= MAX_DEPTH) {
    return /*#__PURE__*/_jsxs(PrimitiveRow, {
      $depth: depth,
      children: [label && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsxs(Small, {
          weight: "demi",
          children: [label, ":"]
        }), ' ']
      }), /*#__PURE__*/_jsx(Small, {
        use: "help",
        children: summarize(value)
      })]
    });
  }
  const isArray = Array.isArray(value);
  const entries = isArray ? value.map((v, i) => [String(i), v]) : Object.entries(value);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(Row, {
      $depth: depth,
      onClick: () => setOpen(prev => !prev),
      type: "button",
      children: [/*#__PURE__*/_jsx(Chevron, {
        $open: open,
        children: /*#__PURE__*/_jsx(RightIcon, {})
      }), label && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsxs(Small, {
          weight: "demi",
          children: [label, ":"]
        }), ' ']
      }), !open && /*#__PURE__*/_jsx(Small, {
        use: "help",
        children: summarize(value)
      })]
    }), open && entries.map(([key, val]) => /*#__PURE__*/_jsx(TreeNode, {
      label: isArray ? undefined : key,
      value: val,
      depth: depth + 1,
      defaultOpen: false
    }, key))]
  });
}
export function PrettyPrintJson({
  data,
  defaultOpen = false
}) {
  const parsed = useMemo(() => {
    if (typeof data === 'string') {
      try {
        return JSON.parse(data);
      } catch (_unused) {
        return data;
      }
    }
    return data;
  }, [data]);
  return /*#__PURE__*/_jsx(TreeNode, {
    value: parsed,
    depth: 0,
    defaultOpen: defaultOpen
  });
}